import { useLayoutEffect, useMemo, useRef, useState, type FocusEvent } from 'react'
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual'
import type { MatterChatHistoryNavItem } from '@/chat-common/conversation_list/hooks/useMatterChatHistory'

type UseChatHistoryVirtualizerOptions = {
  items: MatterChatHistoryNavItem[]
  isLoading: boolean
  menuOpenForId: string | null
}

/** Owns the chat list's scroll geometry and the rows retained for keyboard and menu focus. */
export function useChatHistoryVirtualizer({ items, isLoading, menuOpenForId }: UseChatHistoryVirtualizerOptions) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const newChatRef = useRef<HTMLDivElement>(null)
  // Initial estimates are replaced before paint with the rendered rem-based geometry.
  const [{ rowHeight, rowGap }, setGeometry] = useState({ rowHeight: 36, rowGap: 2 })
  const [focusedChatId, setFocusedChatId] = useState<string | null>(null)

  useLayoutEffect(() => {
    const newChat = newChatRef.current
    const scroll = scrollRef.current
    if (!newChat || !scroll) return

    const measure = () => {
      const rowHeight = newChat.getBoundingClientRect().height
      const rowGap = parseFloat(getComputedStyle(scroll).rowGap) || 0
      setGeometry((previous) => (previous.rowHeight === rowHeight && previous.rowGap === rowGap ? previous : { rowHeight, rowGap }))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(newChat)
    return () => observer.disconnect()
  }, [isLoading])
  const virtualizerOptions = useMemo(() => {
    const focusedIndex = items.findIndex((item) => item.id === focusedChatId)
    const menuIndex = items.findIndex((item) => item.id === menuOpenForId)

    return {
      count: isLoading ? 0 : items.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => rowHeight,
      getItemKey: (index: number) => items[index].id,
      gap: rowGap,
      overscan: 5,
      rangeExtractor: (range: Range) => {
        // Keep both ends for native Tab entry from outside the list. Keeping the focused row's
        // neighbors lets Tab/Shift+Tab move sequentially even across the virtual window edge.
        const indexes = new Set([...defaultRangeExtractor(range), 0, items.length - 1])
        if (focusedIndex !== -1) {
          for (const index of [focusedIndex - 1, focusedIndex, focusedIndex + 1]) {
            if (index >= 0 && index < items.length) indexes.add(index)
          }
        }
        // Radix portals still need their trigger mounted for positioning and focus restoration.
        if (menuIndex !== -1) indexes.add(menuIndex)
        return [...indexes].sort((a, b) => a - b)
      },
    }
  }, [items, isLoading, focusedChatId, menuOpenForId, rowHeight, rowGap])
  const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>(virtualizerOptions)

  const handleRowBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocusedChatId(null)
  }

  return {
    scrollRef,
    newChatRef,
    rowsHeight: virtualizer.getTotalSize(),
    virtualRows: virtualizer.getVirtualItems().map((row) => ({
      key: row.key,
      index: row.index,
      offset: row.start,
    })),
    setFocusedChatId,
    handleRowBlur,
  }
}
